import { galleryImagePath } from "../../api/client";
import { POST_STATUS_LABEL, reactionTotal } from "../gallery/reactions";

import type { TeacherClassGalleryList } from "@canvaschool/contracts";

type Item = TeacherClassGalleryList["items"][number];

const studentLabel = (item: Item) =>
  item.displayName
    ? `${item.studentNumber}번 ${item.displayName}`
    : `${item.studentNumber}번`;

// A post hidden by reports waits for an administrator, not the teacher.
const canTakeDown = (item: Item) =>
  item.status === "PUBLISHED" || item.status === "WITHDRAWN";

/** 「갤러리에 올라간 우리 반 작품」: the teacher can take any of them down. */
export const GalleryPanel = ({
  gallery,
  newCount,
  canManage,
  onTakeDown,
  onAllowAgain,
}: {
  gallery: TeacherClassGalleryList | null;
  /** Posts that appeared since the teacher last looked. */
  newCount: number;
  canManage: boolean;
  onTakeDown: (item: Item) => void;
  onAllowAgain: (item: Item) => void;
}) => (
  <section
    aria-labelledby="canvaschool-class-gallery-heading"
    className="canvaschool-no-print"
  >
    <h2 id="canvaschool-class-gallery-heading">
      갤러리에 올라간 우리 반 작품
      {newCount > 0 && (
        <span
          className="canvaschool-count-badge"
          aria-label={`새 작품 ${newCount}개`}
        >
          새 작품 {newCount}
        </span>
      )}
    </h2>
    <p className="canvaschool-hint">
      학생이 자랑한 작품은 학교와 학급 이름과 함께 다른 학교에도 보여요. 내리면
      학생은 선생님이 다시 허용할 때까지 올릴 수 없어요.
    </p>
    {gallery === null ? (
      <p aria-busy="true">불러오는 중…</p>
    ) : gallery.items.length === 0 ? (
      <p>아직 갤러리에 올라간 작품이 없습니다.</p>
    ) : (
      <ul className="canvaschool-list">
        {gallery.items.map((item) => (
          <li key={item.id} className="canvaschool-gallery-row">
            <img
              className="canvaschool-gallery-thumb"
              src={galleryImagePath(item.id)}
              alt={`${studentLabel(item)} 작품`}
              loading="lazy"
            />
            <div className="canvaschool-gallery-row-body">
              <p className="canvaschool-trash-title">{studentLabel(item)}</p>
              <p className="canvaschool-meta">{item.canvasTitle}</p>
              <p className="canvaschool-meta">
                <span
                  className={
                    item.status === "PUBLISHED"
                      ? "canvaschool-status canvaschool-status--on"
                      : item.status === "HIDDEN"
                      ? "canvaschool-status canvaschool-status--wait"
                      : "canvaschool-status"
                  }
                >
                  {POST_STATUS_LABEL[item.status]}
                </span>
                {" · "}반응 {reactionTotal(item.reactions)}
                {" · "}신고 {item.reportCount}
              </p>
            </div>
            {canManage && canTakeDown(item) && (
              <button
                type="button"
                className="canvaschool-btn-danger"
                aria-label={`${studentLabel(item)} ${item.canvasTitle} 내리기`}
                onClick={() => onTakeDown(item)}
              >
                내리기
              </button>
            )}
            {canManage && item.status === "REMOVED_BY_TEACHER" && (
              <button
                type="button"
                className="canvaschool-btn-secondary"
                aria-label={`${studentLabel(item)} ${
                  item.canvasTitle
                } 다시 허용`}
                onClick={() => onAllowAgain(item)}
              >
                다시 허용
              </button>
            )}
          </li>
        ))}
      </ul>
    )}
  </section>
);
